// ChatTemplates.js - Main template components

import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';

// Styles - use the local scss file
import styles from './chat-templates.scss';

// Components - go up two levels since we're now in chat-templates subfolder
import Button from '../../button/button';
import MbReactDateRange from '../../react-date-range/mb-react-date-range';
import SearchableDropdown from './SearchableDropdown';

// Actions - go up three levels to get to containers
import { fetchChatFilters, fetchDynamicChatFilters } from '../../../containers/chat-agent/actions';

// Utils - go up three levels to get to utils
import getLastPredictionDate from '../../../utils/skus/getLastPredictionDate';
import { forceSingleRetailerSelection } from '../../../utils/other/mbDataUtils';
import getAvailableScreens from '../../../utils/auth/getAvailableScreens';

// Local imports
import {
  SCREEN_TO_VIEW_TYPE,
  FIELD_CONFIG,
  getSimpleOptions,
} from './templateConstants';

import {
  parseTemplate,
  sortFields,
  getDropdownOptions,
  buildQueryFromTemplate,
  getDatePresetOptions,
  formatDateForPicker,
  formatDateForDisplay,
  isTemplateComplete,
} from './templateUtils';

import templatesData from './constants/chatTemplates.json';

// ==============================================
// Simple Select Dropdown (for non-searchable fields)
// ==============================================

const SimpleSelectDropdown = ({ options, value, onChange, placeholder, fieldName }) => (
  <select
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    className={styles.selectDropdown}
  >
    <option value="">{placeholder || `Select ${fieldName}...`}</option>
    {options.map((option) => (
      <option key={option.id} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
);

// ==============================================
// Template Builder Component
// ==============================================

const TemplateBuilderComponent = ({
  template,
  onSubmit,
  onCancel,
  isLightMode,
  // Redux state
  filterDefinitions,
  dynamicBrands,
  dynamicKeywords,
  isLoadingFilters,
  currentScreen,
  brandPerformanceDate,
  brandPerformanceMaxDate,
  marketShareDate,
  marketShareMaxDate,
  // Actions
  fetchChatFilters,
  fetchDynamicChatFilters,
}) => {
  const [values, setValues] = useState({});

  const fields = parseTemplate(template.template, template.category);
  const sortedFields = sortFields(fields);
  const dateFields = sortedFields.filter(f => f.fieldType === 'Date Range');
  const otherFields = sortedFields.filter(f => f.fieldType !== 'Date Range');

  // Determine dates based on template category
  const lastAvailableDate = template.category === 'Sales Performance'
    ? (marketShareDate || brandPerformanceDate)
    : (brandPerformanceDate || marketShareDate);

  const maxDate = template.category === 'Sales Performance'
    ? (marketShareMaxDate || brandPerformanceMaxDate)
    : (brandPerformanceMaxDate || marketShareMaxDate);

  const viewType = SCREEN_TO_VIEW_TYPE[currentScreen] || 11;

  // Fetch filters on mount
  useEffect(() => {
    if (!filterDefinitions || filterDefinitions.length === 0) {
      fetchChatFilters(viewType, null);
    }
  }, [viewType]);

  // Build effective filter definitions with dynamic values
  const effectiveFilterDefinitions = useMemo(() => {
    if (!filterDefinitions?.length) return [];

    return filterDefinitions.map(def => {
      if (['brand', 'brand.keyword', 'brand_name'].includes(def.mbName) && dynamicBrands?.length > 0) {
        return { ...def, values: dynamicBrands };
      }
      if (['search_terms_kpis.search_term', 'searchterm', 'search_term', 'searchTerm', 'keywords', 'keyword'].includes(def.mbName) && dynamicKeywords?.length > 0) {
        return { ...def, values: dynamicKeywords };
      }
      return def;
    });
  }, [filterDefinitions, dynamicBrands, dynamicKeywords]);

  // Populated query for preview
  const populatedQuery = useMemo(() => {
    return buildQueryFromTemplate(template.template, values);
  }, [template.template, values]);

  const handleFieldChange = (fieldName, value) => {
    setValues(prev => ({ ...prev, [fieldName]: value }));

    // Fetch dynamic filters when retailer changes
    const field = fields.find(f => f.fieldName === fieldName);
    const isRetailerChange = field?.fieldType === 'Retailer' || field?.fieldType === 'Retailer Multi';

    if (isRetailerChange && value && fetchDynamicChatFilters) {
      const retailerValue = Array.isArray(value) ? value[0] : value;
      fetchDynamicChatFilters(retailerValue, null, null);
    }
  };

  const handleSubmit = (sendNow) => {
    const finalQuery = buildQueryFromTemplate(template.template, values);
    onSubmit(finalQuery, sendNow);
  };

  const renderField = (field) => {
    const { fieldName, fieldType } = field;
    const isMultiSelect = fieldType.includes('Multi');
    const baseFieldType = fieldType.replace(' Multi', '');
    const config = FIELD_CONFIG[baseFieldType];

    // Date Range Field
    if (config?.type === 'date') {
      const effectiveEndDate = lastAvailableDate
        ? (lastAvailableDate instanceof Date ? lastAvailableDate : moment(lastAvailableDate).toDate())
        : new Date();

      const defaultStartDate = new Date(effectiveEndDate);
      defaultStartDate.setDate(defaultStartDate.getDate() - 30);

      const effectiveMaxDate = maxDate instanceof Date ? maxDate : (maxDate ? new Date(maxDate) : effectiveEndDate);
      const datePresets = getDatePresetOptions(effectiveMaxDate);

      return (
        <div key={fieldName} className={styles.templateField}>
          <label>{fieldName}</label>

          <div className={styles.datePresets}>
            {datePresets.map(preset => (
              <button
                key={preset.id}
                type="button"
                className={styles.datePresetBtn}
                onClick={() => {
                  const { startDate, endDate } = preset.getValue();
                  handleFieldChange(`${fieldName}_start`, startDate);
                  handleFieldChange(`${fieldName}_end`, endDate);
                  handleFieldChange(fieldName, `${formatDateForDisplay(startDate)} to ${formatDateForDisplay(endDate)}`);
                }}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className={styles.dateRangeWrapper}>
            <MbReactDateRange
              selectedDateRange={{
                startDate: values[`${fieldName}_start`] || formatDateForPicker(defaultStartDate),
                endDate: values[`${fieldName}_end`] || formatDateForPicker(effectiveEndDate)
              }}
              maxDate={effectiveMaxDate}
              minDate={null}
              onClickApply={(dateRange) => {
                handleFieldChange(`${fieldName}_start`, dateRange.startDate);
                handleFieldChange(`${fieldName}_end`, dateRange.endDate);
                handleFieldChange(fieldName, `${formatDateForDisplay(dateRange.startDate)} to ${formatDateForDisplay(dateRange.endDate)}`);
              }}
              viewTypeId={0}
              viewTypeIdsNonWeekly={[]}
            />
          </div>
        </div>
      );
    }

    // Simple Dropdown Field
    if (config?.type === 'simple') {
      return (
        <div key={fieldName} className={styles.templateField}>
          <label>{fieldName}</label>
          <SimpleSelectDropdown
            options={getSimpleOptions(baseFieldType)}
            value={values[fieldName] || ''}
            onChange={(value) => handleFieldChange(fieldName, value)}
            placeholder={config.placeholder}
            fieldName={fieldName}
          />
        </div>
      );
    }

    // Searchable Dropdown Field (single or multi)
    const options = getDropdownOptions(effectiveFilterDefinitions, baseFieldType);
    const isLoadingDynamicField = isLoadingFilters && ['Brand Name', 'Search Term'].includes(baseFieldType);

    return (
      <div key={fieldName} className={styles.templateField}>
        <label>
          {fieldName}
          {isMultiSelect && <span className={styles.multiLabel}>(Select multiple)</span>}
        </label>
        <SearchableDropdown
          options={options}
          value={values[fieldName] || (isMultiSelect ? [] : '')}
          onChange={(value) => handleFieldChange(fieldName, value)}
          placeholder={
            isLoadingDynamicField
              ? `Loading ${fieldName}...`
              : `Search ${fieldName}... (${options.length} options)`
          }
          fieldName={fieldName}
          isLoading={isLoadingDynamicField}
          isLightMode={isLightMode}
          isMulti={isMultiSelect}
        />
        {isLoadingDynamicField && (
          <div className={styles.loadingIndicator}>
            <span>Updating options...</span>
          </div>
        )}
      </div>
    );
  };

  const isComplete = isTemplateComplete(fields, values);

  return (
    <div className={`${styles.templateBuilder} ${isLightMode ? styles.lightMode : ''}`}>
      <div className={styles.templateBuilderLeft}>
        <div className={styles.templateHeader}>
          <h4>{template.title}</h4>
          {template.description && (
            <p className={styles.templateDescription}>{template.description}</p>
          )}
          <div className={styles.templateMeta}>
            {template.subCategory && (
              <span className={styles.subCategoryBadge}>{template.subCategory}</span>
            )}
            {template.estimatedTime && (
              <span className={styles.estimatedTime}>⏱️ {template.estimatedTime}</span>
            )}
          </div>
        </div>

        <div className={styles.templateFieldsRow}>
          <div className={styles.templateFieldsLeft}>
            <div className={styles.templateFields}>
              {otherFields.map(renderField)}
            </div>

            {populatedQuery && populatedQuery !== template.template && (
              <div className={styles.queryPreview}>
                <label>Query Preview</label>
                <div className={styles.previewText}>{populatedQuery}</div>
              </div>
            )}
          </div>

          {dateFields.length > 0 && (
            <div className={styles.templateFieldsRight}>
              {dateFields.map(renderField)}
            </div>
          )}
        </div>

        <div className={styles.templateActions}>
          <Button onClick={onCancel} variant="text">Cancel</Button>
          <Button
            onClick={() => handleSubmit(false)}
            variant="pale"
            disabled={!isComplete}
          >
            Copy to Input
          </Button>
          <Button
            onClick={() => handleSubmit(true)}
            variant="contained"
            disabled={!isComplete}
          >
            Send Now
          </Button>
        </div>
      </div>
    </div>
  );
};

// Connect to Redux
export const TemplateBuilder = connect(
  (state) => {
    const currentScreen = state.chatAgent?.currentScreen || null;

    // Brand Performance dates
    const lastProcessedDay = state.brandPerformance?.insights?.insights?.viewDefinitions?.viewMetadata?.lastProcessedDay;
    const brandPerformanceDate = lastProcessedDay || null;
    const brandPerformanceMaxDate = lastProcessedDay ? new Date(lastProcessedDay) : null;

    // Market Share dates
    const domainsMetadata = state.marketShare?.insights?.insights?.viewDefinitions?.viewMetadata?.domainsMetadata;
    const productAttributes = state.marketShare?.insights?.insights?.currentViewConfig?.query?.productAttributes;
    const filterDefs = state.marketShare?.insights?.insights?.viewDefinitions?.filterDefinitions;

    const currentSelectedRetailer = forceSingleRetailerSelection(filterDefs, productAttributes);

    let marketShareDate = null;
    let marketShareMaxDate = null;

    if (domainsMetadata && currentSelectedRetailer) {
      const calculatedDate = getLastPredictionDate(domainsMetadata, currentSelectedRetailer, productAttributes);
      marketShareDate = calculatedDate;
      marketShareMaxDate = calculatedDate;
    }

    return {
      filterDefinitions: state.chatAgent?.filterDefinitions || [],
      dynamicBrands: state.chatAgent?.dynamicBrands || [],
      dynamicKeywords: state.chatAgent?.dynamicKeywords || [],
      isLoadingFilters: state.chatAgent?.isLoadingFilters || false,
      currentScreen,
      brandPerformanceDate,
      brandPerformanceMaxDate,
      marketShareDate,
      marketShareMaxDate,
    };
  },
  (dispatch) => bindActionCreators({
    fetchChatFilters,
    fetchDynamicChatFilters,
  }, dispatch)
)(TemplateBuilderComponent);

// ==============================================
// Template Grid Component
// ==============================================

export const TemplateGrid = ({ templates, onTemplateClick, currentScreen, isLightMode }) => {
  const getInitialCategory = () => {
    if (currentScreen === 'brandPerformance') return 'Visibility Performance';
    if (currentScreen === 'marketShare') return 'Sales Performance';
    return 'All';
  };

  const [selectedCategory, setSelectedCategory] = useState(getInitialCategory());
  const [selectedSubCategory, setSelectedSubCategory] = useState('All');

  const mainCategories = ['All', ...new Set(templates.map(t => t.category))];

  const subCategories = useMemo(() => {
    if (selectedCategory === 'All') return ['All'];
    const subs = templates
      .filter(t => t.category === selectedCategory)
      .map(t => t.subCategory)
      .filter((v, i, a) => v && a.indexOf(v) === i);
    return ['All', ...subs];
  }, [selectedCategory, templates]);

  const filteredTemplates = useMemo(() => {
    let filtered = templates;
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(t => t.category === selectedCategory);
    }
    if (selectedSubCategory !== 'All') {
      filtered = filtered.filter(t => t.subCategory === selectedSubCategory);
    }
    return filtered;
  }, [selectedCategory, selectedSubCategory, templates]);

  useEffect(() => {
    setSelectedSubCategory('All');
  }, [selectedCategory]);

  return (
    <div className={`${styles.templatesFullView} ${isLightMode ? styles.lightMode : ''}`}>
      <div className={styles.templateFilters}>
        <div className={styles.mainCategoryFilters}>
          {mainCategories.map(category => (
            <button
              key={category}
              className={`${styles.categoryFilter} ${selectedCategory === category ? styles.active : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {selectedCategory !== 'All' && subCategories.length > 1 && (
          <div className={styles.subCategoryFilters}>
            {subCategories.map(subCat => (
              <button
                key={subCat}
                className={`${styles.subCategoryFilter} ${selectedSubCategory === subCat ? styles.active : ''}`}
                onClick={() => setSelectedSubCategory(subCat)}
              >
                {subCat}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className={styles.templatesGrid}>
        {filteredTemplates.map((template) => (
          <div
            key={template.id}
            className={styles.templateCard}
            onClick={() => onTemplateClick(template)}
          >
            <div className={styles.templateHeader}>
              <div className={styles.templateBadges}>
                {template.subCategory && (
                  <div className={styles.subCategoryBadge}>{template.subCategory}</div>
                )}
              </div>
              {template.estimatedTime && (
                <div className={styles.estimatedTime}>{template.estimatedTime}</div>
              )}
            </div>
            <div className={styles.templateTitle}>{template.title}</div>
            {template.description && (
              <div className={styles.templateDescription}>{template.description}</div>
            )}
            <div className={styles.templatePreview}>
              {template.template.length > 150
                ? template.template.substring(0, 150) + '...'
                : template.template}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==============================================
// Export Helpers
// ==============================================

export const getTemplateQuestions = () => templatesData;

export const getTemplatesForScreen = (currentScreen) => {
  const allTemplates = getTemplateQuestions();
  const availableScreens = getAvailableScreens();
  
  // Check if user has access to each screen type (using PascalCase as in JWT)
  const hasBrandPerformanceAccess = availableScreens.includes('BrandPerformance');
  const hasMarketShareAccess = availableScreens.includes('MarketShare');
  
  // Collect all templates the user has permission to see
  let allowedTemplates = [];
  
  if (hasBrandPerformanceAccess) {
    allowedTemplates.push(...allTemplates.filter(t => t.category === 'Visibility Performance'));
  }
  
  if (hasMarketShareAccess) {
    allowedTemplates.push(...allTemplates.filter(t => t.category === 'Sales Performance'));
  }
  
  return allowedTemplates;
};

export const getSubCategories = (mainCategory) => {
  const templates = getTemplateQuestions();
  return templates
    .filter(t => t.category === mainCategory)
    .map(t => t.subCategory)
    .filter((v, i, a) => a.indexOf(v) === i);
};

export const getTemplatesForCategory = (category) => {
  const allTemplates = getTemplateQuestions();
  if (!category) return allTemplates;
  return allTemplates.filter(t => t.category === category);
};

export const getTemplatesForSubCategory = (mainCategory, subCategory) => {
  const allTemplates = getTemplateQuestions();
  return allTemplates.filter(t =>
    t.category === mainCategory &&
    (!subCategory || t.subCategory === subCategory)
  );
};

export default {
  TemplateBuilder,
  TemplateGrid,
  getTemplateQuestions,
  getTemplatesForScreen,
  getSubCategories,
  getTemplatesForCategory,
  getTemplatesForSubCategory,
};
